import { createStore } from "vuex";
import { Retiree } from "../retiree";
import ObjectBuilder from "../objectBuilder";
import ScenarioOutcome from "../scenarioOutcome";
import ScenarioType from "../scenarioType";
import { configuration } from "../configuration/configuration";
import { AxiosResponse } from "axios";
import ResponseTracker from "../../../shared/ts/responseTracker";
import View from "../view";
import User from "../user";
import { RetirementPlanResponse } from "../response/retirementPlanResponse";
import { FinishInfoResponse } from "../response/finishInfoResponse";
import { IncomeChartSeries } from "../response/charts/incomeChartSeries";
import { AnnuityProviderResponse } from "../response/annuityProviderResponse";
import { AdviserResponse } from "../response/adviserResponse";
import { Country } from "../country";
import { PensionProviderResponse } from "../response/pensionProviderResponse";
import { Notification as AppNotification } from "../notifications/notification";
import { IncomeSourceChartSeries } from "../response/charts/incomeSourceChartSeries";

const store = createStore({
  state: {
    configuration: configuration,
    id: 0,
    user: new User({ id: -1, roles: [] }),
    retiree: (null as unknown) as Retiree,
    transactionOngoing: false,
    view: View.Haves,
    accordingToPlanScenario: ObjectBuilder.createScenario(ScenarioType.AccordingToPlan, ScenarioOutcome.Unknown),
    lowerGrowthScenario: ObjectBuilder.createScenario(ScenarioType.LowerGrowth, ScenarioOutcome.Unknown),
    youLiveLongerScenario: ObjectBuilder.createScenario(ScenarioType.LiveThreeYearsLonger, ScenarioOutcome.Unknown),
    marketCrashScenario: ObjectBuilder.createScenario(ScenarioType.PotsFallTwentyPercent, ScenarioOutcome.Unknown),
    needSomeCareScenario: ObjectBuilder.createScenario(ScenarioType.NeedExtraMoneyForLastTwoYears, ScenarioOutcome.Unknown),
    annualIncomeSummaryData: new Array<IncomeChartSeries>(),
    potsSummaryData: [],
    annualIncomeByTypeData: new Array<IncomeSourceChartSeries>(),
    annualIncomeBySourceData: new Array<IncomeSourceChartSeries>(),
    plan: null as RetirementPlanResponse | null,
    finishInfo: null as FinishInfoResponse | null,
    pensionProvidersForCostComparison: new Array<PensionProviderResponse>(),
    pensionProvidersForFindYourself: new Array<PensionProviderResponse>(),
    annuityProviders: new Array<AnnuityProviderResponse>(),
    advisers: new Array<AdviserResponse>(),
    countries: new Array<Country>(),
    notifications: new Array<AppNotification>(),
    hasStartedRegistration: false as boolean,
    getRetireeResponse: ResponseTracker.fromAxiosResponse(
      { status: 200, statusText: "" } as AxiosResponse,
      false
    ),
    impersonatingId: null as (number | null)
  },
  mutations: {
    updateTransactionOngoing(state, transactionOngoing) {
      state.transactionOngoing = transactionOngoing;
    },
    updateConfiguration(state, configuration) {
      state.configuration = configuration;
    },
    updateView(state, view: View) {
      state.view = view;
    },
    updateId(state, id: number) {
      state.id = id;
    },
    updateUser(state, user: User) {
      state.user = user;
    },
    updateRetiree(state, retiree) {
      state.retiree = retiree;
    },
    updateAccordingToPlanScenario(state, scenario) {
      state.accordingToPlanScenario = scenario;
    },
    updateLowerGrowthScenario(state, scenario) {
      state.lowerGrowthScenario = scenario;
    },
    updateYouLiveLongerScenario(state, scenario) {
      state.youLiveLongerScenario = scenario;
    },
    updateMarketCrashScenario(state, scenario) {
      state.marketCrashScenario = scenario;
    },
    updateNeedSomeCareScenario(state, scenario) {
      state.needSomeCareScenario = scenario;
    },
    updateAnnualIncomeSummaryData(state, annualIncomeSummaryData: Array<IncomeChartSeries>) {
      state.annualIncomeSummaryData = annualIncomeSummaryData;
    },
    updatePotsSummaryData(state, potsSummaryData) {
      state.potsSummaryData = potsSummaryData;
    },
    updateAnnualIncomeByTypeData(state, annualIncomeByTypeData: Array<IncomeSourceChartSeries>) {
      state.annualIncomeByTypeData = annualIncomeByTypeData;
    },
    updateAnnualIncomeBySourceData(state, annualIncomeBySourceData: Array<IncomeSourceChartSeries>) {
      state.annualIncomeBySourceData = annualIncomeBySourceData;
    },
    updatePlan(state, plan: RetirementPlanResponse | null) {
      state.plan = plan;
    },
    updateFinishInfo(state, finishInfo: FinishInfoResponse | null) {
      state.finishInfo = finishInfo;
    },
    updatePensionProvidersForCostComparison(state, pensionProviders: Array<PensionProviderResponse>) {
      state.pensionProvidersForCostComparison = pensionProviders;
    },
    updatePensionProvidersForFindYourself(state, pensionProviders: Array<PensionProviderResponse>) {
      state.pensionProvidersForFindYourself = pensionProviders;
    },
    updateAnnuityProviders(state, annuityProviders: Array<AnnuityProviderResponse>) {
      state.annuityProviders = annuityProviders;
    },
    updateAdvisers(state, advisers: Array<AdviserResponse>) {
      state.advisers = advisers;
    },
    updateCountries(state, countries: Array<Country>) {
      state.countries = countries;
    },
    updateNotifications(state, notifications: Array<AppNotification>) {
      state.notifications = notifications;
    },
    updateHasStartedRegistration(state, hasStartedRegistration: boolean) {
      state.hasStartedRegistration = hasStartedRegistration;
    },
    updateGetRetireeResponse(state, responseTracker) {
      state.getRetireeResponse = responseTracker;
    },
    updateImpersonatingId(state, id: number | null) {
      state.impersonatingId = id;
    }
  },
  actions: {
    async updateGetRetireeResponse({ commit }, responseTracker) {
      commit("updateGetRetireeResponse", responseTracker);
    },
    async retireeDataChanged({ commit }, retiree: Retiree) {
      commit("updateRetiree", retiree);
      await store.dispatch("updateScenarioOutcomesToUnknown");
      commit("updateAnnualIncomeSummaryData", []);
      commit("updateAnnualIncomeByTypeData", []);
      commit("updateAnnualIncomeBySourceData", []);
      commit("updatePotsSummaryData", []);
      commit("updatePlan", null);
      commit("updateFinishInfo", null);
    },
    async updateScenarioOutcomesToUnknown({ commit }) {
      const updateScenario = function (
        mutationName: string,
        scenarioType: ScenarioType
      ) {
        const scenario = ObjectBuilder.createScenario(
          scenarioType,
          ScenarioOutcome.Unknown
        );
        commit(mutationName, scenario);
      };

      updateScenario("updateAccordingToPlanScenario", ScenarioType.AccordingToPlan);
      updateScenario("updateLowerGrowthScenario", ScenarioType.LowerGrowth);
      updateScenario("updateYouLiveLongerScenario", ScenarioType.LiveThreeYearsLonger);
      updateScenario("updateMarketCrashScenario", ScenarioType.PotsFallTwentyPercent);
      updateScenario("updateNeedSomeCareScenario", ScenarioType.NeedExtraMoneyForLastTwoYears);
    }
  },
  modules: {}
});

export default store;
